import {z} from "zod"

// Memory statistics for a task run (#5843): how much memory was available, how much was used, and whether
// the task ran out — for system RAM and, where there is one, GPU memory. This is the one contract shared by
// the task runner (which measures), the API (which validates and stores), the coordinator and the web app
// (which read). It is stored as a single versioned JSON blob on JobTaskEvent.memoryStats.
//
// All sizes are whole MiB. `v` records the shape a payload was written with; readers are forward-tolerant
// (they read the fields they know and ignore the rest), so `v` only matters for a breaking change, which a
// reader would then migrate in code.

export const MEMORY_STATS_VERSION = 2

/** The largest size accepted, in MiB (4 TiB): anything above it is a reporting bug, not a machine. */
export const MAX_MEMORY_MB = 4 * 1024 * 1024

/** Which memory: the machine's system RAM, or the GPU's memory. */
export const memoryKindSchema = z.enum(["system", "gpu"])
export type MemoryKind = z.infer<typeof memoryKindSchema>

/**
 * How system RAM "used" was measured. `exact`: the kernel's high-watermark for just this task. `sampled`:
 * periodic readings of the whole worker, which may miss a brief spike and may include memory retained from
 * earlier tasks.
 */
export const memoryPrecisionSchema = z.enum(["exact", "sampled"])
export type MemoryPrecision = z.infer<typeof memoryPrecisionSchema>

const megabytes = z.number().finite().nonnegative().max(MAX_MEMORY_MB)

/** System RAM: the most in use while the task ran, the limit it ran against, and how "used" was measured. */
export const systemMemoryUsageSchema = z.object({
    usedMb: megabytes,
    availableMb: megabytes.optional(),
    precision: memoryPrecisionSchema,
})
export type SystemMemoryUsage = z.infer<typeof systemMemoryUsageSchema>

/**
 * GPU memory: the most in use on the device while the task ran — always sampled, and counting every
 * process on the GPU (on a single-task GPU machine, that is the task) — and the GPU's total memory.
 */
export const gpuMemoryUsageSchema = z.object({
    usedMb: megabytes,
    availableMb: megabytes.optional(),
})
export type GpuMemoryUsage = z.infer<typeof gpuMemoryUsageSchema>

/** The limits a runner found when it started a task, before anything was used: recorded on the Running event. */
export const memoryLimitsSchema = z.object({
    systemMb: megabytes.optional(),
    gpuMb: megabytes.optional(),
})
export type MemoryLimits = z.infer<typeof memoryLimitsSchema>

export const taskMemoryStatsSchema = z
    .object({
        v: z.number().int().positive(),
        limits: memoryLimitsSchema.optional(),
        system: systemMemoryUsageSchema.optional(),
        gpu: gpuMemoryUsageSchema.optional(),
        /** Present when the task ran out of memory; `kind` says which. */
        oom: z.object({kind: memoryKindSchema}).optional(),
    })
    .passthrough()
export type TaskMemoryStats = z.infer<typeof taskMemoryStatsSchema>

/** The memory statistics in a stored payload, or undefined when there are none or they can't be read. */
export const readMemoryStats = (value: unknown): TaskMemoryStats | undefined => {
    if (value == null) return undefined
    const parsed = taskMemoryStatsSchema.safeParse(value)
    return parsed.success ? parsed.data : undefined
}

/** How each kind of memory is named to the user, mid-sentence: "Out of memory (system RAM)". */
export const MEMORY_KIND_LABEL: Record<MemoryKind, string> = {system: "system RAM", gpu: "GPU"}

/** MiB as a compact size: from 1 GiB up in GiB with one decimal (a trailing .0 dropped), else whole MiB. */
export const formatMebibytes = (mb: number): string => (mb >= 1024 ? `${(mb / 1024).toFixed(1).replace(/\.0$/, "")} GiB` : `${Math.round(mb)} MiB`)
